import { ZfIcon, ZfListItem, ZfLoadingSpinner, ZfStack } from '@zafire/react';
import type { CSSProperties, ReactNode } from 'react';
import { useMemo } from 'react';

interface IListItem<TData> {
    title?: string;
    description?: string;
    data: TData;
}

interface IListProps<TData> {
    title?: string;
    items: IListItem<TData>[];
    titleSpacing?: 'large' | 'extra-large' | 'extra-extra-large' | 'medium';
    size?: 'small' | 'medium' | 'large';
    selectedIndex?: number;
    isLoading?: boolean;
    onItemClick?: (data: TData, index: number) => void;
    isSelected?: (data: TData) => boolean;
    hasBorderBottom?: (index: number) => boolean;
    getItemKey?: (data: TData, index: number) => string | number;
    renderTrailing?: (data: TData, index: number) => ReactNode;
    containerStyle?: CSSProperties;
    style?: CSSProperties;
}

export const List = <TData,>({
    title,
    items,
    titleSpacing = 'large',
    size = 'small',
    selectedIndex,
    isLoading,
    onItemClick,
    isSelected,
    hasBorderBottom,
    getItemKey,
    renderTrailing,
    containerStyle,
    style,
}: IListProps<TData>) => {
    const { itemSize, iconSize, itemPadding } = useMemo<{
        itemSize: 'small' | 'medium' | 'large';
        iconSize: 'extra-large' | 'large' | 'medium';
        itemPadding: string;
    }>(() => {
        if (size === 'small') return { itemSize: 'small', iconSize: 'medium', itemPadding: 'var(--padding-vertical-md)' };
        if (size === 'medium') return { itemSize: 'medium', iconSize: 'large', itemPadding: 'var(--padding-vertical-lg)' };

        return { itemSize: 'large', iconSize: 'extra-large', itemPadding: 'var(--padding-vertical-xxl)' };
    }, [size]);

    if (isLoading) return <ZfLoadingSpinner />;

    return (
        <ZfStack direction="column" spacing={titleSpacing} style={containerStyle}>
            {title ? <h5>{title}</h5> : null}
            <ZfStack direction="column" style={style}>
                {items.map((item, index) => {
                    const selected = isSelected ? isSelected(item.data) : selectedIndex === index;

                    return (
                        <ZfListItem
                            key={getItemKey?.(item.data, index) ?? `${item?.title}${item?.description}${index}`}
                            size={itemSize}
                            titleText={item.title}
                            description={item.description}
                            select={selected}
                            state={selected ? 'selected' : 'default'}
                            autoChangeState={false}
                            onListItemSelect={() => onItemClick?.(item.data, index)}
                            style={{
                                borderBottom: hasBorderBottom?.(index) ? '1px solid var(--border-color-default-normal)' : '',
                            }}
                        >
                            {renderTrailing ? (
                                renderTrailing(item.data, index)
                            ) : (
                                <ZfIcon slot="trailing" name="chevron_right" size={iconSize} style={{ padding: `${itemPadding} 0` }} />
                            )}
                        </ZfListItem>
                    );
                })}
            </ZfStack>
        </ZfStack>
    );
};
